import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ArrowRight, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import LanguageToggle from './LanguageToggle'

const Navigation = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { name: t('navigation.home'), href: '/' },
    { name: t('navigation.features'), href: '/features' },
    // { name: t('navigation.pricing'), href: '/pricing' },
    { name: t('navigation.build'), href: '/build' },
    { 
      name: t('navigation.industries'), 
      href: '/industries',
      submenu: [
        { name: t('navigation.industriesSubmenu.realEstate'), href: '/industries/real-estate' },
        { name: t('navigation.industriesSubmenu.automobile'), href: '/industries/automobile' },
        { name: t('navigation.industriesSubmenu.education'), href: '/industries/education' },
        { name: t('navigation.industriesSubmenu.hospitality'), href: '/industries/hospitality' },
        { name: t('navigation.industriesSubmenu.healthcare'), href: '/industries/healthcare' }
      ]
    },
    { name: t('navigation.contact'), href: '/contact' }
  ]

  const handleNavClick = (href) => {
    if (href.startsWith('/#')) {
      // Handle anchor links for home page sections
      if (location.pathname !== '/') {
        window.location.href = href
      } else {
        const element = document.querySelector(href.substring(1))
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }
    setIsOpen(false)
  }

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-lg' 
          : 'bg-gradient-to-b from-black/20 via-black/10 to-transparent backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-3 cursor-pointer"
            >
              <div className="relative">
                <img 
                  src={scrolled ? "/konvo.png" : "/new.png"}
                  alt="KonvoAI Logo" 
                  className={`h-12 w-auto transition-all duration-300 ${
                    scrolled 
                      ? 'brightness-100 contrast-100' 
                      : 'brightness-110 contrast-125 drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]'
                  }`}
                />
              </div>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6 rtl:space-x-reverse">
            {navItems.map((item) => (
              item.submenu ? (
                <div key={item.name} className="relative group">
                  <div 
                    className={`flex items-center space-x-1 cursor-pointer font-medium transition-colors duration-300 ${
                      scrolled 
                        ? 'text-gray-700 hover:text-purple-600'
                        : 'text-white/90 hover:text-white drop-shadow-sm'
                    }`}
                    onMouseEnter={() => setActiveDropdown(item.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <Link to={item.href}>
                      <motion.span whileHover={{ y: -2 }}>
                        {item.name}
                      </motion.span>
                    </Link>
                    <ChevronDown size={16} />
                  </div>
                  
                  <AnimatePresence>
                    {activeDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                        onMouseEnter={() => setActiveDropdown(item.name)}
                        onMouseLeave={() => setActiveDropdown(null)}
                      >
                        <div className="py-1">
                          {item.submenu.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : item.href.startsWith('/') && !item.href.startsWith('/#') ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`font-medium transition-colors duration-300 ${
                    scrolled 
                      ? 'text-gray-700 hover:text-purple-600'
                      : 'text-white/90 hover:text-white drop-shadow-sm'
                  }`}
                >
                  <motion.span whileHover={{ y: -2 }}>
                    {item.name}
                  </motion.span>
                </Link>
              ) : (
                <motion.button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  whileHover={{ y: -2 }}
                  className={`font-medium transition-colors duration-300 cursor-pointer ${
                    scrolled 
                      ? 'text-gray-700 hover:text-purple-600'
                      : 'text-white/90 hover:text-white drop-shadow-sm'
                  }`}
                >
                  {item.name}
                </motion.button>
              )
            ))}
            
            <LanguageToggle />
            
            <Link to="/inquiry">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-full font-medium flex items-center space-x-2 rtl:space-x-reverse hover:shadow-lg transition-shadow backdrop-blur-sm shadow-md hover:shadow-purple-500/20"
              >
                <span>{t('navigation.getStarted')}</span>
                <ArrowRight size={16} className="rtl:rotate-180" />
              </motion.button>
            </Link>
          </div>

          {/* Mobile: Language Toggle and Menu Button */}
          <div className="md:hidden flex items-center space-x-3 rtl:space-x-reverse">
            <LanguageToggle />
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 transition-colors duration-300 ${
                scrolled ? 'text-gray-700' : 'text-white drop-shadow-sm'
              }`}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white/95 backdrop-blur-lg border-t"
          >
            <div className="px-4 py-4 space-y-4">
              {navItems.map((item) => (
                item.submenu ? (
                  <div key={item.name}>
                    <div 
                      className="flex items-center justify-between text-gray-700 hover:text-purple-600 font-medium py-2 w-full text-left cursor-pointer"
                      onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                    >
                      <Link 
                        to={item.href}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOpen(false);
                        }}
                      >
                        <motion.span
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          {item.name}
                        </motion.span>
                      </Link>
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''}`}
                      />
                    </div>
                    
                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="pl-6 space-y-1 mt-1"
                        >
                          {item.submenu.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block py-2 text-sm text-gray-600 hover:text-purple-600"
                              onClick={() => setIsOpen(false)}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : item.href.startsWith('/') && !item.href.startsWith('/#') ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className="block text-gray-700 hover:text-purple-600 font-medium py-2 w-full text-left"
                  >
                    <motion.span
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      {item.name}
                    </motion.span>
                  </Link>
                ) : (
                  <motion.button
                    key={item.name}
                    onClick={() => handleNavClick(item.href)}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="block text-gray-700 hover:text-purple-600 font-medium py-2 w-full text-left cursor-pointer"
                  >
                    {item.name}
                  </motion.button>
                )
              ))}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(false)}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-full font-medium flex items-center justify-center space-x-2 rtl:space-x-reverse mt-4 shadow-md hover:shadow-purple-500/20"
              >
                <span>{t('navigation.getStarted')}</span>
                <ArrowRight size={16} className="rtl:rotate-180" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}

export default Navigation
